import React, {useEffect, useState} from 'react';
import {Dialog} from '@bosch/react-frok';
import {t} from 'i18next';
import {CheckboxStyled, DeleteInfoStyled} from './DeleteItemDialog.style';
import {Accordion} from '../../Accordion/Accordion';
import {config} from '../../../init/config';
import {CountryPicker} from '../../LocalePicker/CountryPicker';
import {LanguagePicker} from '../../LocalePicker/LanguagePicker';
import {LocalePicker} from '../../LocalePicker/LocalePicker';
import {convertLocaleToRepresentationValue, isNullOrEmpty} from '../../../utils';
import {generateIdentifier} from '../../../utils/IdentityUtils';
import {DropdownOptionProps} from '../../../models/DropdownProps';

export interface DeleteDialogProps {
    readonly fileTitle: string;
    readonly countryDataSource: DropdownOptionProps[];
    readonly languageDataSource: DropdownOptionProps[];
    readonly localeDataSource: string[];
    readonly defaultLocale: string;
    readonly isCompleteToDelete: boolean;
    readonly countriesToDelete: string[];
    readonly languagesToDelete: string[];
    readonly localesToDelete: string[];
    setIsCompleteToDelete: (isComplete: boolean) => void;
    setCountriesToDelete: (countriesToDelete: string[]) => void;
    setLanguagesToDelete: (languagesToDelete: string[]) => void;
    setLocalesToDelete: (localesToDelete: string[]) => void;
    onCancel: () => void;
    onConfirm: () => void;
}

const filterLocales = (locales: string[]) => {
    let filteredLocales: string[] = [];
    let localeItem: string[] = [];
    if ((locales) && (locales.length > 0)) {
        filteredLocales = locales
            .map(locale => locale)
            .filter(locale => {
                localeItem = locale.split('_');
                if (localeItem.length >= 2) {
                    return (((config.DLS_COUNTRIES.length === 0)
                            || ((config.DLS_COUNTRIES.length > 0) && (config.DLS_COUNTRIES.includes(localeItem[1].toUpperCase()))))
                        && ((config.DLS_LANGUAGES.length === 0)
                            || ((config.DLS_LANGUAGES.length > 0) && (config.DLS_LANGUAGES.includes(localeItem[0].toLowerCase())))))
                }

                return false;
            });
    }

    return filteredLocales;
};

/**
 * @name DeleteItemDialog
 * @description
 * A dialog to delete the selected file/folder
 */
export const DeleteItemDialog: React.VFC<DeleteDialogProps> = (props) => {
    const defaultLocale = convertLocaleToRepresentationValue(props.defaultLocale);
    const getCountryOptions = (): DropdownOptionProps[] => {
        return props.countryDataSource
            .filter(country => ((config.DLS_COUNTRIES.length === 0)
                || ((config.DLS_COUNTRIES.length > 0) && (config.DLS_COUNTRIES.includes(country.value.toUpperCase())))))
            .filter(country => ((props.countriesToDelete.length === 0)
                || ((props.countriesToDelete.length > 0) && (!props.countriesToDelete.includes(country.value.toUpperCase())))));
    };

    const getLanguageOptions = (): DropdownOptionProps[] => {
        return props.languageDataSource
            .filter(language => ((config.DLS_LANGUAGES.length === 0)
                || ((config.DLS_LANGUAGES.length > 0) && (config.DLS_LANGUAGES.includes(language.value.toLowerCase())))))
            .filter(language => ((props.languagesToDelete.length === 0)
                || ((props.languagesToDelete.length > 0) && (!props.languagesToDelete.includes(language.value.toLowerCase())))));
    };

    const getLocaleDataSource = () => {
        let locales: string[] = [];
        if ((props.localeDataSource) && (props.localeDataSource.length > 0)) {
            locales = filterLocales(props.localeDataSource);
        }

        return locales;
    };

    const getLocaleOptions = (): DropdownOptionProps[] => {
        let localeOptions: DropdownOptionProps[] = [];
        let localeItem: string[] = [];
        getLocaleDataSource()
            .filter(language => ((props.localesToDelete.length === 0)
                || ((props.localesToDelete.length > 0) && (!props.localesToDelete.includes(language)))))
            .map(locale => {
                if (locale) {
                    localeItem = locale.toLowerCase().split('_');
                    if ((localeItem) && (localeItem.length >= 2)) {
                        let localeValue = convertLocaleToRepresentationValue(locale);
                        if (!isNullOrEmpty(localeValue)) {
                            localeOptions.push({name: localeValue, value: localeValue} as DropdownOptionProps);
                        }
                    }
                }
            });

        return localeOptions;
    };

    const [countryOptions, setCountryOptions] = useState<DropdownOptionProps[]>(getCountryOptions());
    const [languageOptions, setLanguageOptions] = useState<DropdownOptionProps[]>(getLanguageOptions());
    const [localeOptions, setLocaleOptions] = useState<DropdownOptionProps[]>(getLocaleOptions());

    useEffect(() => {
        setCountryOptions(getCountryOptions());
    }, [props.countriesToDelete]);

    useEffect(() => {
        setLanguageOptions(getLanguageOptions());
    }, [props.languagesToDelete]);

    useEffect(() => {
        setLocaleOptions(getLocaleOptions());
    }, [props.localesToDelete]);

    const onChangeIsCompleteHandler = () => {
        props.setIsCompleteToDelete(!props.isCompleteToDelete);
    };

    return (
        <Dialog id={generateIdentifier()}
                modal
                open
                title={t('DELETE_DIALOG_TITLE', {fileTitle: props.fileTitle})}
                headline={t('DELETE_DIALOG_HEADLINE', {fileTitle: props.fileTitle})}
                cancelLabel={t('GENERAL_CANCEL_LABEL')}
                confirmLabel={t('DELETE_DIALOG_DELETE_LABEL')}
                onClose={props.onCancel}
                onCancel={props.onCancel}
                onConfirm={props.onConfirm}>
            <DeleteInfoStyled id={generateIdentifier()}>
                {t('DELETE_DIALOG_DELETION_PERMANENT_INFO_TEXT', {locale: defaultLocale})}
            </DeleteInfoStyled>
            <CheckboxStyled id={generateIdentifier('DeleteDialogIsComplete')}
                            key={'DeleteDialogIsComplete'}
                            label={t('DELETE_DIALOG_IS_COMPLETE')}
                            checked={props.isCompleteToDelete}
                            onChange={onChangeIsCompleteHandler}/>
            <Accordion accordionHeadline={t('LOCALE_CAPTION')}>
                <DeleteInfoStyled id={generateIdentifier()}>
                    {t('DELETE_DIALOG_DELETION_DEFAULT_LOCALE_PERMANENT_INFO_TEXT', {locale: defaultLocale})}
                </DeleteInfoStyled>
                <CountryPicker dataSource={countryOptions}
                               defaultValue={''}
                               resetState={props.isCompleteToDelete}
                               resetInput={true}
                               disabled={(props.isCompleteToDelete || (countryOptions.length === 0))}
                               onChange={props.setCountriesToDelete}/>
                <LanguagePicker dataSource={languageOptions}
                                defaultValue={''}
                                resetState={props.isCompleteToDelete}
                                resetInput={true}
                                disabled={(props.isCompleteToDelete || (languageOptions.length === 0))}
                                onChange={props.setLanguagesToDelete}/>
                <LocalePicker dataSource={localeOptions}
                              defaultValue={''}
                              resetState={props.isCompleteToDelete}
                              resetInput={true}
                              disabled={(props.isCompleteToDelete || (localeOptions.length === 0))}
                              onChange={props.setLocalesToDelete}/>
            </Accordion>
        </Dialog>
    );
};
